import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import type { MeResponse } from '@sklad-sync/shared';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CategoryBoostPanel } from './CategoryBoostPanel';

type Props = { user: MeResponse };

type ProductLite = {
  shopitem_id: number;
  name: string;
  image_url: string | null;
  parent_code: string | null;
};

type FeaturedWeek = {
  id: number;
  starts_at: number;
  ends_at: number;
  discount_value: number;
  discount_unit: 'percent' | 'czk';
  note: string | null;
  created_at: number;
  created_by: string;
  applied_at: number | null;
  reverted_at: number | null;
  sync_error: string | null;
  products: ProductLite[];
  status: 'active' | 'upcoming' | 'past';
};

type SyncRunResult = {
  status: 'success' | 'partial' | 'failed' | 'dry_run';
  applied_count: number;
  reverted_count: number;
  error_count: number;
  duration_ms: number;
  error_message: string | null;
  details: string[];
};

// ─── Datum helpery ───────────────────────────────────────────────────────

// Převod unix ms ↔ HTML datetime-local hodnota ("YYYY-MM-DDTHH:MM").
function unixToLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localInputToUnix(s: string): number {
  // Konstruktor `new Date(string)` parsuje YYYY-MM-DDTHH:MM jako lokální čas.
  return new Date(s).getTime();
}

// Default: pondělí 00:00 nejbližšího týdne → neděle 23:59:59 stejného týdne.
function defaultWeekRange(): { start: number; end: number } {
  const now = new Date();
  const day = now.getDay(); // 0 = neděle, 1 = pondělí…
  const daysToMonday = day === 0 ? 1 : (8 - day) % 7 || 7;
  const start = new Date(now);
  start.setDate(now.getDate() + daysToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 0);
  return { start: start.getTime(), end: end.getTime() };
}

// ─── Stránka ─────────────────────────────────────────────────────────────

export function ProduktTydnePage(_: Props) {
  const [weeks, setWeeks] = useState<FeaturedWeek[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [editing, setEditing] = useState<FeaturedWeek | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ weeks: FeaturedWeek[] }>('/api/produkt-tydne');
      setWeeks(data.weeks);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(id: number) {
    if (!confirm('Opravdu smazat tuto akci?')) return;
    try {
      await apiFetch(`/api/produkt-tydne/${id}`, { method: 'DELETE' });
      setToast({ msg: 'Akce smazána', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  const [running, setRunning] = useState(false);
  async function manualSync() {
    setRunning(true);
    try {
      const r = await apiFetch<SyncRunResult>('/api/produkt-tydne/sync-run', { method: 'POST' });
      const tag = r.status === 'dry_run' ? '(DRY RUN) ' : '';
      const msg =
        r.error_count > 0
          ? `${tag}Sync skončil s chybami — ${r.applied_count} aplikováno, ${r.reverted_count} vráceno, ${r.error_count} chyb. ${r.error_message ?? ''}`
          : `${tag}Sync OK — ${r.applied_count} aplikováno, ${r.reverted_count} vráceno (${r.duration_ms} ms)`;
      setToast({ msg, kind: r.error_count > 0 ? 'error' : 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Sync selhal', kind: 'error' });
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Produkt týdne</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Týdenní akce — vybrané produkty se slevou a vyšší prioritou.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={manualSync}
            disabled={running}
            className="px-3 py-2 bg-surface border border-border text-text-secondary hover:text-text hover:border-primary/40 text-sm font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            title="Spustí sync se Shoptetem ručně, bez čekání na cron"
          >
            {running ? 'Synchronizuji…' : '⟳ Sync Shoptet'}
          </button>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
          >
            + Nová akce
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {weeks == null ? (
        <div className="bg-surface rounded-xl border border-border p-10 text-center text-text-muted text-sm">
          Načítám…
        </div>
      ) : weeks.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border p-10 text-center">
          <div className="text-text-muted text-sm">Zatím tu nejsou žádné akce.</div>
          <div className="text-text-muted text-xs mt-1">
            Klikni na „Nová akce" a naplánuj první týden.
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {weeks.map((w) => (
            <WeekRow
              key={w.id}
              week={w}
              onEdit={() => setEditing(w)}
              onDelete={() => remove(w.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <WeekModal
          week={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            setToast({ msg, kind: 'success' });
            await load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}
      <CategoryBoostPanel onToast={(msg, kind) => setToast({ msg, kind })} />
      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

// ─── Řádek v seznamu ─────────────────────────────────────────────────────

function WeekRow({
  week,
  onEdit,
  onDelete,
}: { week: FeaturedWeek; onEdit: () => void; onDelete: () => void }) {
  const discountLabel =
    week.discount_unit === 'percent'
      ? `−${formatNumber(week.discount_value)} %`
      : `−${formatNumber(week.discount_value)} Kč`;
  const statusLabel =
    week.status === 'active' ? 'Aktivní' : week.status === 'upcoming' ? 'Plánovaná' : 'Skončila';
  const statusCls =
    week.status === 'active'
      ? 'text-success bg-success-light border-success/30'
      : week.status === 'upcoming'
        ? 'text-primary bg-primary-50 border-primary/30'
        : 'text-text-muted bg-bg border-border';
  return (
    <div className="bg-surface border border-border rounded-lg p-3 flex items-stretch gap-3">
      {/* Stav + období + sleva */}
      <div className="shrink-0 w-64 flex flex-col gap-1 self-center">
        <div className="flex items-center gap-1 flex-wrap">
          <span className={`text-[11px] font-semibold rounded px-2 py-0.5 border ${statusCls}`}>
            {statusLabel}
          </span>
          {/* Sync stav vůči Shoptetu — applied/reverted/sync_error */}
          {week.sync_error && (
            <span
              className="text-[11px] font-semibold rounded px-2 py-0.5 border text-danger bg-danger-light border-danger/30"
              title={week.sync_error}
            >
              ⚠ sync error
            </span>
          )}
          {!week.sync_error && week.applied_at && !week.reverted_at && (
            <span
              className="text-[11px] font-semibold rounded px-2 py-0.5 border text-success bg-success-light border-success/30"
              title={`Aplikováno v Shoptetu ${fmtDateTime(week.applied_at)}`}
            >
              ✓ v Shoptetu
            </span>
          )}
          {!week.sync_error && week.applied_at && week.reverted_at && (
            <span
              className="text-[11px] font-semibold rounded px-2 py-0.5 border text-text-muted bg-bg border-border"
              title={`Vráceno v Shoptetu ${fmtDateTime(week.reverted_at)}`}
            >
              ↩ vráceno
            </span>
          )}
        </div>
        <div className="text-sm font-semibold text-text">{discountLabel}</div>
        <div className="text-[11px] text-text-muted">
          {fmtDateTime(week.starts_at)} → {fmtDateTime(week.ends_at)}
        </div>
      </div>

      {/* Produkty */}
      <div className="flex gap-2 overflow-x-auto flex-1 min-w-0 items-start py-0.5">
        {week.products.length === 0 ? (
          <div className="text-xs text-text-muted self-center">žádné produkty</div>
        ) : (
          week.products.map((p) => (
            <div
              key={p.shopitem_id}
              className="shrink-0 w-20 flex flex-col items-center gap-0.5"
              title={p.name}
            >
              <div className="w-14 h-14 bg-bg border border-border rounded-md flex items-center justify-center overflow-hidden">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-[9px] text-text-muted">—</span>
                )}
              </div>
              <div className="text-[10px] font-mono text-text-muted truncate w-full text-center">
                {p.parent_code ?? '—'}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Poznámka (krátká) */}
      <div className="shrink-0 w-32 self-center text-[11px] text-text-muted">
        {week.note ? (
          <div className="line-clamp-3" title={week.note}>
            {week.note}
          </div>
        ) : (
          <span className="italic">bez poznámky</span>
        )}
      </div>

      {/* Akce */}
      <div className="shrink-0 flex items-center gap-2 self-center">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-text-secondary hover:text-primary border border-border rounded-md px-2.5 py-1 cursor-pointer"
        >
          Upravit
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-text-secondary hover:text-danger border border-border rounded-md px-2.5 py-1 cursor-pointer"
        >
          Smazat
        </button>
      </div>
    </div>
  );
}

function formatNumber(n: number): string {
  // 20 → "20"; 19.5 → "19,5"
  return Number.isInteger(n)
    ? String(n)
    : n.toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

// ─── Modal (vytvořit / upravit) ──────────────────────────────────────────

function WeekModal({
  week,
  onClose,
  onSaved,
  onError,
}: {
  week: FeaturedWeek | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isNew = week === null;
  const defaultRange = defaultWeekRange();
  const [startsAt, setStartsAt] = useState(unixToLocalInput(week?.starts_at ?? defaultRange.start));
  const [endsAt, setEndsAt] = useState(unixToLocalInput(week?.ends_at ?? defaultRange.end));
  const [discountValue, setDiscountValue] = useState<string>(
    week ? String(week.discount_value) : '',
  );
  const [discountUnit, setDiscountUnit] = useState<'percent' | 'czk'>(
    week?.discount_unit ?? 'percent',
  );
  const [note, setNote] = useState(week?.note ?? '');
  const [products, setProducts] = useState<ProductLite[]>(week?.products ?? []);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    const startsAtMs = localInputToUnix(startsAt);
    const endsAtMs = localInputToUnix(endsAt);
    if (!Number.isFinite(startsAtMs) || !Number.isFinite(endsAtMs)) {
      setLocalError('Datum začátku/konce není platné');
      return;
    }
    if (endsAtMs <= startsAtMs) {
      setLocalError('Konec akce musí být po začátku');
      return;
    }
    const dv = Number(discountValue.replace(',', '.'));
    if (!Number.isFinite(dv) || dv <= 0) {
      setLocalError('Sleva musí být kladné číslo');
      return;
    }
    if (discountUnit === 'percent' && dv > 100) {
      setLocalError('Procentní sleva nesmí být větší než 100 %');
      return;
    }
    if (products.length === 0) {
      setLocalError('Vyber alespoň jeden produkt');
      return;
    }

    const body = {
      starts_at: startsAtMs,
      ends_at: endsAtMs,
      discount_value: dv,
      discount_unit: discountUnit,
      note: note.trim() || null,
      shopitem_ids: products.map((p) => p.shopitem_id),
    };

    setSaving(true);
    try {
      if (isNew) {
        await apiFetch('/api/produkt-tydne', { method: 'POST', body });
        onSaved('Akce vytvořena');
      } else {
        await apiFetch(`/api/produkt-tydne/${week.id}`, { method: 'PATCH', body });
        onSaved('Změny uloženy');
      }
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-5">
          {isNew ? 'Nová akce Produkt týdne' : 'Upravit akci'}
        </h2>
        <hr className="border-border mb-5" />

        <form onSubmit={submit} className="space-y-4">
          {/* Produkty */}
          <div>
            <div className="text-sm font-medium text-text-secondary block mb-1.5">
              Produkty týdne (může jich být víc)
            </div>
            <ProductsMultiPicker selected={products} onChange={setProducts} />
          </div>

          {/* Datum začátku / konce */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="ft-starts"
                className="text-sm font-medium text-text-secondary block mb-1.5"
              >
                Začátek akce
              </label>
              <input
                id="ft-starts"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                required
                className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label
                htmlFor="ft-ends"
                className="text-sm font-medium text-text-secondary block mb-1.5"
              >
                Konec akce
              </label>
              <input
                id="ft-ends"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                required
                className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>

          {/* Sleva — hodnota + jednotka */}
          <div>
            <div className="text-sm font-medium text-text-secondary block mb-1.5">Výše slevy</div>
            <div className="flex gap-2">
              <input
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                min="0"
                step="0.01"
                required
                placeholder="např. 20"
                className="flex-1 text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <select
                value={discountUnit}
                onChange={(e) => setDiscountUnit(e.target.value === 'czk' ? 'czk' : 'percent')}
                className="text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
              >
                <option value="percent">%</option>
                <option value="czk">Kč</option>
              </select>
            </div>
          </div>

          {/* Poznámka */}
          <div>
            <label
              htmlFor="ft-note"
              className="text-sm font-medium text-text-secondary block mb-1.5"
            >
              Poznámka (volitelné)
            </label>
            <textarea
              id="ft-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all resize-y"
            />
          </div>

          {localError && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-3 py-2">
              {localError}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer disabled:opacity-50"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Multi-product picker ────────────────────────────────────────────────
// Vlastní jednoduchá verze (úsporná, bez drag-drop). Vyhledává přes
// /api/produkt-tydne/products — vyžaduje modul produkt_tydne.

function ProductsMultiPicker({
  selected,
  onChange,
}: { selected: ProductLite[]; onChange: (next: ProductLite[]) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductLite[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: '20' });
        const data = await apiFetch<{ products: ProductLite[] }>(
          `/api/produkt-tydne/products?${params.toString()}`,
        );
        setResults(data.products);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query]);

  function add(p: ProductLite) {
    if (selected.some((s) => s.shopitem_id === p.shopitem_id)) return;
    onChange([...selected, p]);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function remove(shopitem_id: number) {
    onChange(selected.filter((s) => s.shopitem_id !== shopitem_id));
  }

  return (
    <div>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Začni psát kód nebo název (od 2 znaků)"
          className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
        />
        {open && results.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-64 overflow-y-auto">
            {results.map((p) => {
              const already = selected.some((s) => s.shopitem_id === p.shopitem_id);
              return (
                <button
                  key={p.shopitem_id}
                  type="button"
                  onClick={() => !already && add(p)}
                  disabled={already}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                    already
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-surface-hover cursor-pointer'
                  }`}
                >
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-8 h-8 object-cover rounded shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{p.name}</div>
                    <div className="text-[11px] font-mono text-text-muted">
                      {p.parent_code ?? '—'}
                    </div>
                  </div>
                  {already && (
                    <div className="text-[11px] text-text-muted shrink-0">již vybráno</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">
            hledám…
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-text-muted mb-1.5">
            Vybráno {selected.length} {selected.length === 1 ? 'produkt' : 'produktů'}
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.map((p) => (
              <div
                key={p.shopitem_id}
                className="flex items-center gap-2 px-2 py-1 bg-primary-50 border border-primary/30 rounded-md text-xs text-primary"
              >
                {p.image_url && (
                  <img src={p.image_url} alt="" className="w-5 h-5 object-cover rounded" />
                )}
                <span className="max-w-[220px] truncate">{p.name}</span>
                <button
                  type="button"
                  onClick={() => remove(p.shopitem_id)}
                  className="text-primary hover:text-danger ml-1 cursor-pointer"
                  aria-label="Odebrat"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
